import { motion, useMotionValue, useReducedMotion, useScroll, useSpring, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, Check } from "lucide-react";
import hero from "@/assets/hero.jpg";
import { INTAKE_URLS } from "@/config/intake";
import { MEMBERSHIP } from "@/config/pricing";
import { LiveJoinCounter } from "@/components/landing/LiveJoinCounter";
import { StateEligibility } from "@/components/landing/StateEligibility";

const MEMBER_PERKS = [
  "Member pricing on every protocol",
  "Quarterly labs included",
  "Care team in your pocket",
];

export const Hero = () => {
  const reduced = useReducedMotion();
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], reduced ? ["0%", "0%"] : ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], reduced ? [1, 1] : [1, 1.08]);
  const textY = useTransform(scrollYProgress, [0, 1], reduced ? ["0%", "0%"] : ["0%", "-12%"]);
  const fade = useTransform(scrollYProgress, [0, 0.85], reduced ? [1, 1] : [1, 0]);

  const magX = useMotionValue(0);
  const magY = useMotionValue(0);
  const sx = useSpring(magX, { stiffness: 220, damping: 18, mass: 0.4 });
  const sy = useSpring(magY, { stiffness: 220, damping: 18, mass: 0.4 });
  // Cache rect on enter to avoid getBoundingClientRect on every mousemove
  // (which forces a layout flush each frame while transforms are animating).
  const magRect = useRef<{ cx: number; cy: number } | null>(null);
  const onMagEnter = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (reduced) return;
    if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
    const r = e.currentTarget.getBoundingClientRect();
    magRect.current = { cx: r.left + r.width / 2, cy: r.top + r.height / 2 };
  };
  const onMagMove = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const r = magRect.current;
    if (!r) return;
    const dx = e.clientX - r.cx;
    const dy = e.clientY - r.cy;
    magX.set(Math.max(-12, Math.min(12, dx * 0.25)));
    magY.set(Math.max(-8, Math.min(8, dy * 0.25)));
  };
  const onMagLeave = () => {
    magRect.current = null;
    magX.set(0);
    magY.set(0);
  };
  const today = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  return (
    <section ref={ref} className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden md:grain">
      <div className="absolute inset-0 -z-10 bg-gradient-warm" />
      <div className="hidden md:block absolute -top-40 -right-40 -z-10 h-[520px] w-[520px] rounded-full bg-accent/10 blur-3xl" />

      <motion.div style={{ y: textY, opacity: fade }} className="container-wide grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-6 max-w-xl">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-xs text-foreground/70"
          >
            <span className="relative flex size-2">
              <span className="absolute inset-0 rounded-full bg-accent/60 motion-safe:animate-pulse-soft" />
              <span className="relative inline-flex size-2 rounded-full bg-accent" />
            </span>
            One membership · ${MEMBERSHIP.monthly}/mo · cancel anytime
          </motion.div>

          <h1
            className="font-display text-[clamp(2.6rem,6.2vw,5rem)] leading-[1.0] mt-6 text-foreground text-balance"
          >
            One membership.<br />
            <span className="text-foreground/85">Every protocol modern medicine has to offer.</span>
          </h1>

          <p className="mt-6 text-lg text-foreground/70 leading-relaxed max-w-md">
            GLP-1s, peptides, hormones, longevity. Prescribed by US-licensed clinicians,
            compounded at FDA-regulated pharmacies, shipped to your door.
          </p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-9 flex flex-col sm:flex-row sm:items-center gap-4"
          >
            <motion.a
              href={INTAKE_URLS.default}
              target="_blank"
              rel="noopener noreferrer"
              onMouseEnter={onMagEnter}
              onMouseMove={onMagMove}
              onMouseLeave={onMagLeave}
              style={{ x: sx, y: sy }}
              className="group relative overflow-hidden inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-4 text-sm font-medium hover:opacity-90 transition will-change-transform"
            >
              <span aria-hidden className="pointer-events-none absolute inset-y-0 -inset-x-4 w-1/3 bg-gradient-to-r from-transparent via-primary-foreground/25 to-transparent hidden md:block motion-safe:animate-sheen" />
              <span className="relative">Start membership — $0 today</span>
              <ArrowRight className="relative size-4 transition-transform group-hover:translate-x-0.5" />
            </motion.a>
            <a
              href="#how"
              className="text-sm text-foreground/65 hover:text-foreground transition underline-offset-4 hover:underline"
            >
              How it works ↓
            </a>
          </motion.div>

          <StateEligibility />

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-10 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground/55"
          >
            {[
              "FDA-regulated pharmacies",
              "US-licensed clinicians",
              "HIPAA-secure",
              "Free shipping",
            ].map((t) => (
              <div key={t} className="flex items-center gap-1.5 leading-snug">
                <span className="size-1 rounded-full bg-accent" />
                {t}
              </div>
            ))}
          </motion.div>
          <p className="mt-4 text-[11px] uppercase tracking-[0.22em] text-foreground/45 font-medium">
            As of <time dateTime={new Date().toISOString().slice(0,10)}>{today}</time> · accepting new members
          </p>
          <div className="mt-4">
            <LiveJoinCounter />
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 1.02 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, ease: "easeOut" }}
          className="lg:col-span-6 relative"
        >
          <div className="relative aspect-[4/5] sm:aspect-[5/6] lg:aspect-[4/5] rounded-[1.75rem] overflow-hidden shadow-soft">
            <motion.div style={{ y: imgY, scale: imgScale }} className="absolute -inset-4 will-change-transform">
              <img
                src={hero}
                alt="A calm woman in linen by a sunlit window, mid-breath"
                width={1536}
                height={1536}
                fetchPriority="high"
                decoding="async"
                className="size-full object-cover motion-safe:animate-breathe"
              />
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-t from-foreground/30 via-transparent to-transparent" />

            {/* Membership value card */}
            <div className="absolute left-4 right-4 bottom-4 md:left-6 md:right-6 md:bottom-6 rounded-2xl bg-background/95 backdrop-blur-md border border-border/60 p-5 md:p-6 overflow-hidden">
              <span aria-hidden className="pointer-events-none absolute inset-y-0 -left-1/4 w-1/3 bg-gradient-to-r from-transparent via-accent/15 to-transparent motion-safe:animate-sheen" />
              <div className="relative flex items-center justify-between mb-4">
                <span className="text-[10px] uppercase tracking-[0.22em] text-foreground/55 font-medium">
                  Olive Membership
                </span>
                <span className="text-[10px] text-foreground/45">Cancel anytime</span>
              </div>
              <div className="flex items-baseline gap-1.5 mb-4">
                <span className="font-display text-4xl md:text-5xl text-foreground leading-none">
                  ${MEMBERSHIP.monthly}
                </span>
                <span className="text-foreground/55 text-sm">/ month</span>
                <span className="text-foreground/45 text-xs ml-1">
                  or ${MEMBERSHIP.annual}/yr
                </span>
              </div>
              <ul className="space-y-2">
                {MEMBER_PERKS.map((p) => (
                  <li key={p} className="flex items-start gap-2 text-[12.5px] text-foreground/75 leading-snug">
                    <Check className="size-3.5 text-accent mt-0.5 shrink-0" strokeWidth={2.5} />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};
